const MAX_HTTP_LINK_LENGTH = 16_384;

const bracketPairs = new Map([
  [')', '('],
  [']', '['],
  ['}', '{'],
  ['»', '«'],
  ['›', '‹'],
  ['」', '「'],
  ['』', '『'],
]);
const bracketOpenings = new Set(bracketPairs.values());

const wrapperPairs = new Map([
  ['(', ')'],
  ['[', ']'],
  ['{', '}'],
  ['«', '»'],
  ['‹', '›'],
  ['「', '」'],
  ['『', '』'],
  ['‘', '’'],
  ['“', '”'],
  ["'", "'"],
]);

function trimEnclosingWrapper(
  candidate: string,
  content: string,
  start: number,
) {
  const enclosingClosings = new Set<string>();
  for (let index = start - 1; index >= 0; index--) {
    const closing = wrapperPairs.get(content.charAt(index));
    if (!closing) break;
    enclosingClosings.add(closing);
  }
  if (!enclosingClosings.size) return candidate;

  const openings: string[] = [];
  let offset = 0;
  for (const character of candidate) {
    const expectedOpening = bracketPairs.get(character);
    if (expectedOpening && openings.at(-1) === expectedOpening) {
      openings.pop();
    } else if (enclosingClosings.has(character)) {
      const isQuote = /['’”]/u.test(character);
      const following = candidate.slice(offset + character.length);
      if (
        !isQuote ||
        !following ||
        /^[\s\p{Sentence_Terminal}\p{Pd}\p{Pe}\p{Pf},;:…，、；：]/u.test(
          following,
        )
      ) {
        return candidate.slice(0, offset);
      }
    }
    if (bracketOpenings.has(character)) openings.push(character);
    offset += character.length;
  }
  return candidate;
}

function trimHostnameProse(candidate: string, schemeLength: number) {
  const afterScheme = candidate.slice(schemeLength);
  const authorityEnd = afterScheme.search(/[/?#\\]/);
  const authority =
    authorityEnd === -1 ? afterScheme : afterScheme.slice(0, authorityEnd);
  const hostStart = schemeLength + authority.lastIndexOf('@') + 1;
  const hostAndPort = authority.slice(authority.lastIndexOf('@') + 1);
  const proseBoundary = hostAndPort.search(/[,;!…，、；：！？]|(?!-)\p{Pd}/u);
  if (proseBoundary !== -1) {
    return candidate.slice(0, hostStart + proseBoundary);
  }
  const symbolSuffix = hostAndPort.match(/(?:\p{S}|\u{200d}|\u{fe0f})+$/u);
  if (authorityEnd === -1 && symbolSuffix?.index !== undefined) {
    const hostPrefix = hostAndPort.slice(0, symbolSuffix.index);
    if (/:\d+$/u.test(hostPrefix)) {
      return candidate.slice(0, hostStart + symbolSuffix.index);
    }
  }
  return candidate;
}

function containsAsciiControl(candidate: string) {
  for (const character of candidate) {
    const code = character.charCodeAt(0);
    if (code < 32 || code === 127) return true;
  }
  return false;
}

function trimProseSuffix(candidate: string) {
  const characters = Array.from(candidate);
  const openings: string[] = [];
  const unmatchedClosings = new Set<number>();
  for (const [index, character] of characters.entries()) {
    if (bracketOpenings.has(character)) {
      openings.push(character);
    } else if (bracketPairs.has(character)) {
      if (openings.at(-1) === bracketPairs.get(character)) openings.pop();
      else unmatchedClosings.add(index);
    }
  }

  let end = characters.length;
  while (
    end > 0 &&
    (/[\p{Sentence_Terminal},;'’”…，、；]/u.test(characters[end - 1]) ||
      unmatchedClosings.has(end - 1))
  ) {
    end--;
  }
  return characters.slice(0, end).join('');
}

export default function findHttpLinks(content: string) {
  const links: { content: string; index: number }[] = [];
  const schemes = /https?:\/\//gi;
  const candidatePattern = /[^\s<>"`]+/uy;
  let consumedUntil = 0;

  for (const scheme of content.matchAll(schemes)) {
    if (scheme.index < consumedUntil) continue;

    candidatePattern.lastIndex = scheme.index;
    let candidate = candidatePattern.exec(content)?.[0];
    if (!candidate) continue;

    candidate = trimEnclosingWrapper(candidate, content, scheme.index);

    const adjacentLink = candidate.search(/[,;](?=https?:\/\/)/i);
    const queryOrFragment = candidate.search(/[?#]/);
    if (
      adjacentLink !== -1 &&
      (queryOrFragment === -1 || adjacentLink < queryOrFragment)
    ) {
      candidate = candidate.slice(0, adjacentLink);
    }

    consumedUntil = scheme.index + candidate.length;
    if (candidate.length > MAX_HTTP_LINK_LENGTH) continue;
    if (containsAsciiControl(candidate)) continue;

    const link = trimProseSuffix(
      trimHostnameProse(trimProseSuffix(candidate), scheme[0].length),
    );

    try {
      const parsed = new URL(link);
      if (!parsed.hostname) continue;
    } catch {
      continue;
    }
    links.push({ content: link, index: scheme.index });
  }

  return links;
}
